// Thin client for the Fastify API: JSON in and out, bearer token, Turkish errors.

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Fired when the API wants a new password before anything else (see SessionGate). */
export const PASSWORD_REQUIRED_EVENT = 'wms:password-required';

let token: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setToken(value: string | null, unauthorized?: () => void): void {
  token = value;
  if (unauthorized) onUnauthorized = unauthorized;
}

export interface RequestOptions {
  /** verify-and-pack answers a refused scan with 404/422 and a normal { ok: false } body */
  readonly refusalIsAnswer?: boolean;
  /** A person's action on the floor: every try of it carries its Idempotency-Key, and it learns how the try ended */
  readonly action?: FloorAction;
  /** Gives up after this long (ms); a floor action waits ACTION_TIMEOUT_MS */
  readonly timeoutMs?: number;
}

/** How long a floor action waits for its answer: a phone on a dropping connection must not hang. */
export const ACTION_TIMEOUT_MS = 12_000;

/**
 * How the latest try of a floor action ended: the server counted it, refused it (nothing was booked),
 * or no answer came (the connection dropped: it may or may not have counted).
 */
export type ActionOutcome = 'counted' | 'refused' | 'lost';

/**
 * One action of a person on the floor: a scan, "tamamla", "kargoya ver". Every try of it carries the
 * same Idempotency-Key, so when the connection drops before the answer the screen sends it again and
 * the server counts it once; the answer of a repeat tells whether the first try had counted.
 */
export interface FloorAction {
  readonly key: string;
  /** the latest try (null: not sent yet) */
  outcome: ActionOutcome | null;
  /** the latest answer was the stored one of an earlier try: that try had counted */
  replayed: boolean;
  /** api() calls made with it (one try may be two requests: a route's /start, then the stop) */
  tries: number;
  /** the latest answer's error code; null when it counted or no answer came */
  code: string | null;
  /**
   * Set by the screen: it already said what the latest answer means in its own words (KARGOYA
   * VERMEYİN, a barcode to teach), so no general "Bağlantı koptu — … SAYILMADI" goes next to it.
   */
  answered: boolean;
}

export function floorAction(): FloorAction {
  return { key: newKey(), outcome: null, replayed: false, tries: 0, code: null, answered: false };
}

/** Sent with a request this action already sent once ("POST /api/…"): the server then knows it is a repeat. */
export const REPEAT_HEADER = 'idempotency-repeat';
/** On an answer given after the server looked the key up; one without it came before (a session refusal, a failure). */
export const CHECKED_HEADER = 'idempotent-checked';

/** The requests each action has sent: the server keeps a key's answer per request. */
const sentBy = new WeakMap<FloorAction, Set<string>>();

/** The session ended or must change first: the screen is about to go, nothing more is sent from it. */
const SESSION_REFUSALS = new Set(['UNAUTHENTICATED', 'SESSION_ENDED', 'PASSWORD_CHANGE_REQUIRED', 'TWO_FACTOR_REQUIRED']);
export const sessionRefused = (action: FloorAction) => action.outcome !== 'counted' && action.code !== null && SESSION_REFUSALS.has(action.code);

/** A UUID v4. crypto.randomUUID needs https; a phone on the warehouse network may open the app over http. */
function newKey(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** The proxy's own answer (502/503/504 without the API's error body): the API restarted or did not answer in time. */
const GATEWAY = 'GATEWAY';

/** The answer came from no server: the connection dropped or took too long (whether it counted is not known). */
export const connectionLost = (error: unknown) => error instanceof ApiError && (error.status === 0 || error.code === 'IN_PROGRESS' || error.code === GATEWAY);

/**
 * verify-and-pack counted the scan, then refused (the last unit is in, the label is not ready yet):
 * only that refusal carries the scan it booked.
 */
function bookedRefusal(data: unknown): boolean {
  const verification = (data as { verification?: { lastScan?: unknown } | null } | undefined)?.verification;
  return verification?.lastScan !== undefined && verification.lastScan !== null;
}

/** How a try ended, from its answer. */
function outcomeOf(response: Response, code: string | undefined, a: { gateway: boolean; repeat: boolean; replayed: boolean; booked: boolean }): ActionOutcome {
  if (response.ok || a.booked) return 'counted';
  // 409 "işleniyor": the first try still runs, its answer is not here yet
  if (code === 'IN_PROGRESS' || a.gateway) return 'lost';
  // a repeat stopped before the server looked its key up, for a reason of the moment (the session
  // ended meanwhile, a failure on the way in), says nothing about the first try; a malformed request
  // would have been refused the first time too
  const beforeKey = !a.replayed && response.headers.get(CHECKED_HEADER) !== 'true';
  const ofTheMoment = response.status === 401 || response.status === 403 || response.status === 429 || response.status >= 500;
  return a.repeat && beforeKey && ofTheMoment ? 'lost' : 'refused';
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export async function api<T>(method: Method, path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
  const { action } = options;
  const timeoutMs = options.timeoutMs ?? (action ? ACTION_TIMEOUT_MS : undefined);
  const abort = timeoutMs === undefined ? null : new AbortController();
  const timer = abort ? setTimeout(() => abort.abort(), timeoutMs) : undefined;
  const keyed = action !== undefined && method !== 'GET';
  // this very request went out before with this key: whatever comes back is a repeat's answer
  let repeat = false;
  if (action) {
    action.tries += 1;
    action.outcome = 'lost';
    action.replayed = false;
    action.code = null;
    action.answered = false;
    const sent = sentBy.get(action) ?? new Set<string>();
    repeat = sent.has(`${method} ${path}`);
    sentBy.set(action, sent.add(`${method} ${path}`));
  }
  let response: Response;
  let text: string;
  try {
    response = await fetch(path, {
      method,
      headers: {
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(keyed ? { 'idempotency-key': action.key } : {}),
        ...(keyed && repeat ? { [REPEAT_HEADER]: '1' } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(abort ? { signal: abort.signal } : {}),
    });
    // the answer's body is part of the wait too
    text = response.status === 204 ? '' : await response.text();
  } catch {
    throw abort?.signal.aborted
      ? new ApiError(0, 'TIMEOUT', `Sunucu ${Math.round((timeoutMs ?? 0) / 1000)} saniyede cevap vermedi. Ağ bağlantısını kontrol edin.`)
      : new ApiError(0, 'OFFLINE', 'Sunucuya ulaşılamıyor. Ağ bağlantısını kontrol edin.');
  } finally {
    clearTimeout(timer);
  }
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = undefined;
  }
  const error = response.ok ? undefined : (data as { error?: { code?: string; message?: string; details?: unknown } } | undefined)?.error;
  // the proxy answered for the API (Caddy's 502 while it restarts): whatever was sent may have counted
  const gateway = response.status >= 502 && response.status <= 504 && typeof error?.code !== 'string';
  if (action) {
    action.replayed = response.headers.get('idempotent-replayed') === 'true';
    action.code = response.ok ? null : (error?.code ?? `HTTP_${response.status}`);
    action.outcome = outcomeOf(response, error?.code, { gateway, repeat: repeat && keyed, replayed: action.replayed, booked: options.refusalIsAnswer === true && bookedRefusal(data) });
  }
  if (response.status === 204) return undefined as T;
  if (!response.ok) {
    if (options.refusalIsAnswer && data && typeof data === 'object' && 'ok' in data) return data as T;
    if (gateway) throw new ApiError(response.status, GATEWAY, 'Sunucu şu an cevap veremiyor. Birkaç saniye sonra tekrar deneyin.');
    if (response.status === 401 && token) onUnauthorized?.();
    // an admin gave this user a temporary password meanwhile, or reset the second factor of a role
    // that needs it: the session reloads into the password / setup screen
    if (error?.code === 'PASSWORD_CHANGE_REQUIRED' || error?.code === 'TWO_FACTOR_REQUIRED') window.dispatchEvent(new Event(PASSWORD_REQUIRED_EVENT));
    throw new ApiError(response.status, error?.code ?? `HTTP_${response.status}`, error?.message ?? 'İstek başarısız oldu', error?.details);
  }
  return data as T;
}

/** A document as bytes (PDF / ZPL), with the session's token; a body makes it a POST. */
export async function apiBlob(path: string, body?: unknown): Promise<Blob> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: body === undefined ? 'GET' : 'POST',
      headers: { ...(token ? { authorization: `Bearer ${token}` } : {}), ...(body === undefined ? {} : { 'content-type': 'application/json' }) },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new ApiError(0, 'OFFLINE', 'Sunucuya ulaşılamıyor. Ağ bağlantısını kontrol edin.');
  }
  if (!response.ok) {
    const error = ((await response.json().catch(() => undefined)) as { error?: { code?: string; message?: string } } | undefined)?.error;
    throw new ApiError(response.status, error?.code ?? `HTTP_${response.status}`, error?.message ?? 'Belge alınamadı');
  }
  return response.blob();
}

/** Problems the API lists next to its message (e.g. orders that could not be released). */
export function errorDetails(error: unknown): string[] {
  if (!(error instanceof ApiError) || !Array.isArray(error.details)) return [];
  return error.details
    .map((d: unknown) => {
      if (d && typeof d === 'object') {
        const item = d as { reason?: unknown; message?: unknown };
        return String(item.reason ?? item.message ?? '');
      }
      return String(d);
    })
    .filter(Boolean);
}

export const errorMessage = (error: unknown) => (error instanceof Error ? error.message : 'Beklenmeyen bir hata oluştu');
